import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import TorchMeter from '../../TorchMeter';

import uiHPSPImg from '../../assets/UI_HPSP.png';
import uiHPSPGlitchImg from '../../assets/UI_HPSP_glitch.png';
import minimapIconImg from '../../assets/minimap.png';
import minimapGlitchImg from '../../assets/minimap_glitch.png';
import legendariumIconImg from '../../assets/legendarium.png';
import legendariumGlitchImg from '../../assets/legendarium_glitch.png';
import backpackIcon from '../../assets/backpack.png';
import backpackGlitchIcon from '../../assets/backpack_glitch.png';
import keyIcon from '../../assets/key_icon.png';
import teethIcon from '../../assets/teeth_icon.png';
import potionIcon from '../../assets/potion_icon.png';
import topLeftUiDecoration from '../../assets/ui/top_left.png';
import bottomRightUiDecoration from '../../assets/ui/bottom_right.png';
import textEnvelopImg from '../../assets/ui/text_envelop.png';
import loreBannerImg from '../../assets/lore/lore_banner.png';

import {
  stopBoundaryClick,
  stopBoundaryPointerDown,
} from '../../game/input/dungeonClickBoundary';

import {
  getRelicRarity,
  formatRelicRarity,
} from '../../game/relics/relicRarity';

const HudHoverTooltip = ({ open, className = '', children }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.15 }}
        className={`absolute z-50 bg-black bg-opacity-80 p-3 rounded-lg border border-yellow-500 text-sm text-gray-300 font-karma pointer-events-none select-none ${className}`}
      >
        {children}
      </motion.div>
    )}
  </AnimatePresence>
);

const EXPLORATION_FEEDBACK_DURATION_MS = 1150;
const BACKPACK_FEEDBACK_DURATION_MS = 1150;
const OBJECTIVE_BANNER_DURATION_MS = 9000;
const OBJECTIVE_BANNER_EXIT_MS = 360;

const OBJECTIVE_BANNER_CONFIG_BY_SOURCE = {
  brazier: {},
  cleansing: {},
  corruption: {},
  yahaka: {},
  lore: {
    imageSrc: loreBannerImg,
    imageWidth: 'min(84vw, 392px)',
    imageMaxWidth: '392px',
    textTopOffset: '124px',
    textClassName: 'text-[26px] md:text-[42px] leading-[1.03]',
    textBoxStyle: {
      width: 'min(74vw, 360px)',
      maxWidth: '360px',
      minHeight: 'clamp(32px, 4.5vw, 46px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
};

const EXPLORATION_FLOATING_FEEDBACK_ANCHORS = {
  hp: { left: '19.5%', top: '46%' },
  sp: { left: '80.3%', top: '11%' },
};

const EXPLORATION_FLOATING_FEEDBACK_COLORS = {
  damage: 'rgba(255,255,255,0.98)',
  gain: 'rgba(198,255,198,0.98)',
};

const BACKPACK_FLOATING_FEEDBACK_COLORS = {
  loss: 'rgba(255,245,245,0.98)',
  gain: 'rgba(198,255,198,0.98)',
};

const BACKPACK_RESOURCE_ICONS = {
  keys: keyIcon,
  teeth: teethIcon,
  potions: potionIcon,
};

const EXPLORATION_FLASH_STYLES = {
  damage: {
    border: 'rgba(205, 42, 42, 0.95)',
    glow: 'rgba(225, 72, 72, 0.68)',
    fill: 'rgba(185, 22, 22, 0.18)',
  },
  heal: {
    border: 'rgba(58, 188, 92, 0.95)',
    glow: 'rgba(102, 232, 132, 0.68)',
    fill: 'rgba(34, 150, 66, 0.18)',
  },
  sp: {
    border: 'rgba(138, 88, 255, 0.96)',
    glow: 'rgba(182, 134, 255, 0.70)',
    fill: 'rgba(102, 54, 214, 0.18)',
  },
};

function positionExplorationFloatingFeedback(items) {
  return items.map((item) => {
    const siblings = items.filter((candidate) => candidate.anchor === item.anchor);
    const index = siblings.findIndex((candidate) => candidate.id === item.id);
    const offsetX = (index - (siblings.length - 1) / 2) * 34;
    return { ...item, offsetX };
  });
}

function positionBackpackFloatingFeedback(items) {
  return items.map((item) => {
    const siblings = items.filter((candidate) => candidate.resource === item.resource);
    const index = siblings.findIndex((candidate) => candidate.id === item.id);
    const offsetX = (index - (siblings.length - 1) / 2) * 34;
    return { ...item, offsetX };
  });
}

function positionClosedBackpackFloatingFeedback(items) {
  return items.map((item, index) => ({
    ...item,
    offsetX: (index - (items.length - 1) / 2) * 56,
  }));
}

function ExplorationScreenFlash({ tick, tone }) {
  if (!tick || !EXPLORATION_FLASH_STYLES[tone]) return null;

  const style = EXPLORATION_FLASH_STYLES[tone];

  return (
    <motion.div
      key={`${tone}-${tick}`}
      className="fixed inset-0 pointer-events-none overflow-visible"
      style={{ zIndex: 46, isolation: 'isolate' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: [0, 1, 0] }}
      transition={{ duration: 0.32, ease: 'easeOut' }}
      aria-hidden="true"
    >
      <div
        className="absolute inset-0"
        style={{
          background: [
            `linear-gradient(to bottom, ${style.border} 0%, ${style.glow} 10%, ${style.fill} 22%, rgba(0,0,0,0) 50%)`,
            `linear-gradient(to top, ${style.border} 0%, ${style.glow} 10%, ${style.fill} 22%, rgba(0,0,0,0) 50%)`,
            `linear-gradient(to right, ${style.border} 0%, ${style.glow} 9%, ${style.fill} 20%, rgba(0,0,0,0) 48%)`,
            `linear-gradient(to left, ${style.border} 0%, ${style.glow} 9%, ${style.fill} 20%, rgba(0,0,0,0) 48%)`,
            `radial-gradient(circle at top left, ${style.glow} 0%, ${style.fill} 22%, rgba(0,0,0,0) 58%)`,
            `radial-gradient(circle at top right, ${style.glow} 0%, ${style.fill} 22%, rgba(0,0,0,0) 58%)`,
            `radial-gradient(circle at bottom left, ${style.glow} 0%, ${style.fill} 22%, rgba(0,0,0,0) 58%)`,
            `radial-gradient(circle at bottom right, ${style.glow} 0%, ${style.fill} 22%, rgba(0,0,0,0) 58%)`,
          ].join(', '),
          filter: 'blur(14px) saturate(1.06)',
          transform: 'translateZ(0)',
        }}
      />
    </motion.div>
  );
}

function BackpackFloatingFeedback({
  items,
  isMobile,
  withIcon = false,
}) {
  if (!items.length) return null;

  return (
    <AnimatePresence initial={false}>
      {items.map((item) => {
        const iconSrc = BACKPACK_RESOURCE_ICONS[item.resource];
        return (
          <div
            key={item.id}
            className="absolute pointer-events-none"
            style={{
              left: '50%',
              top: 0,
              transform: `translate(-50%, -50%) translateX(${item.offsetX}px)`,
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.92 }}
              animate={{ opacity: [0, 1, 0], y: [10, 0, -28], scale: [0.92, 1, 1.02] }}
              exit={{ opacity: 0 }}
              transition={{ duration: BACKPACK_FEEDBACK_DURATION_MS / 1000, ease: 'easeOut' }}
              className="font-karma font-extrabold leading-none whitespace-nowrap flex items-center justify-center"
              style={{
                gap: withIcon ? (isMobile ? '5px' : '6px') : 0,
                color: BACKPACK_FLOATING_FEEDBACK_COLORS[item.tone] || BACKPACK_FLOATING_FEEDBACK_COLORS.loss,
                fontSize: isMobile ? '20px' : '24px',
                letterSpacing: '0.3px',
                textShadow:
                  '1px 0 0 rgba(0,0,0,1), -1px 0 0 rgba(0,0,0,1), 0 1px 0 rgba(0,0,0,1), 0 -1px 0 rgba(0,0,0,1), 1px 1px 0 rgba(0,0,0,1), -1px 1px 0 rgba(0,0,0,1), 1px -1px 0 rgba(0,0,0,1), -1px -1px 0 rgba(0,0,0,1), 0 8px 18px rgba(0,0,0,0.72)',
              }}
            >
              {withIcon && iconSrc && (
                <img
                  src={iconSrc}
                  alt=""
                  aria-hidden="true"
                  draggable={false}
                  style={{
                    width: isMobile ? '20px' : '24px',
                    height: isMobile ? '20px' : '24px',
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.75))',
                  }}
                />
              )}
              <span>{item.text}</span>
            </motion.div>
          </div>
        );
      })}
    </AnimatePresence>
  );
}

const HealthDisplay = ({
  hp,
  maxHp,
  sp,
  maxSp,
  sanityFx,
  sanityHudGlitchBurst,
  isMobile,
  onOpenStatus,
  onTriggerScreenFlash,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const prevVitalsRef = React.useRef(null);
  const floatingSeqRef = React.useRef(0);
  const pulseSeqRef = React.useRef(0);
  const floatingTimeoutsRef = React.useRef({});

  const [floatingFeedbackItems, setFloatingFeedbackItems] = React.useState([]);

  const positionedFloatingFeedbackItems = React.useMemo(
    () => positionExplorationFloatingFeedback(floatingFeedbackItems),
    [floatingFeedbackItems]
  );

  const addFloatingFeedback = React.useCallback((anchor, text, tone) => {
    floatingSeqRef.current += 1;
    const id = `exploration-float-${floatingSeqRef.current}`;

    setFloatingFeedbackItems((prev) => [...prev, { id, anchor, text, tone }]);

    const timeoutId = window.setTimeout(() => {
      setFloatingFeedbackItems((prev) => prev.filter((item) => item.id !== id));
      delete floatingTimeoutsRef.current[id];
    }, EXPLORATION_FEEDBACK_DURATION_MS);

    floatingTimeoutsRef.current[id] = timeoutId;
  }, []);

  React.useEffect(() => {
    return () => {
      for (const timeoutId of Object.values(floatingTimeoutsRef.current)) {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  React.useEffect(() => {
    const nextHp = Math.max(0, Math.floor(Number(hp) || 0));
    const nextSp = Math.max(0, Math.floor(Number(sp) || 0));
    const prev = prevVitalsRef.current;

    if (!prev) {
      prevVitalsRef.current = { hp: nextHp, sp: nextSp };
      return;
    }

    const hpLoss = Math.max(0, prev.hp - nextHp);
    const hpGain = Math.max(0, nextHp - prev.hp);
    const spLoss = Math.max(0, prev.sp - nextSp);
    const spGain = Math.max(0, nextSp - prev.sp);

    if (hpLoss > 0) {
      addFloatingFeedback('hp', `-${hpLoss}`, 'damage');
      pulseSeqRef.current += 1;
      onTriggerScreenFlash?.('damage', pulseSeqRef.current);
    }

    if (hpGain > 0) {
      addFloatingFeedback('hp', `+${hpGain}`, 'gain');
      pulseSeqRef.current += 1;
      onTriggerScreenFlash?.('heal', pulseSeqRef.current);
    }

    if (spLoss > 0) {
      addFloatingFeedback('sp', `-${spLoss}`, 'damage');
    }

    if (spGain > 0) {
      addFloatingFeedback('sp', `+${spGain}`, 'gain');
      pulseSeqRef.current += 1;
      onTriggerScreenFlash?.('sp', pulseSeqRef.current);
    }

    prevVitalsRef.current = { hp: nextHp, sp: nextSp };
  }, [hp, sp, addFloatingFeedback]);

  return (
    <motion.div
      className="relative"
      animate={
        sanityHudGlitchBurst
          ? {
              x: Number(sanityHudGlitchBurst.jitterX),
              y: Number(sanityHudGlitchBurst.jitterY),
            }
          : {
              x: 0,
              y: 0,
            }
      }
      transition={{
        duration: sanityHudGlitchBurst
          ? sanityHudGlitchBurst.durationMs / 1000
          : 0.12,
        ease: 'easeOut',
      }}
      onMouseEnter={!isMobile ? () => setIsHovered(true) : undefined}
      onMouseLeave={!isMobile ? () => setIsHovered(false) : undefined}
    >
      <button
        type="button"
        onClick={(e) => stopBoundaryClick(e, onOpenStatus)}
        className="relative block p-0 m-0 bg-transparent border-0 outline-none focus:outline-none active:outline-none appearance-none"
        style={{
          WebkitTapHighlightColor: 'transparent',
          backgroundColor: 'transparent',
        }}
        aria-label="Open status panel"
      >
        <img
          src={sanityHudGlitchBurst ? uiHPSPGlitchImg : uiHPSPImg}
          alt="Health Display"
          className="w-[170px] h-[155px] md:w-[219px] md:h-[200px]"
          draggable={false}
          loading="lazy"
        />
      </button>

      <div
        className="absolute inset-0 pointer-events-none select-none"
        aria-hidden="true"
      >
        <AnimatePresence initial={false}>
          {positionedFloatingFeedbackItems.map((item) => {
            const anchorStyle = EXPLORATION_FLOATING_FEEDBACK_ANCHORS[item.anchor] || EXPLORATION_FLOATING_FEEDBACK_ANCHORS.hp;
            return (
              <div
                key={item.id}
                className="absolute"
                style={{
                  left: anchorStyle.left,
                  top: anchorStyle.top,
                  transform: `translate(-50%, -50%) translateX(${item.offsetX}px)`,
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.92 }}
                  animate={{ opacity: [0, 1, 0], y: [10, 0, -28], scale: [0.92, 1, 1.02] }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: EXPLORATION_FEEDBACK_DURATION_MS / 1000, ease: 'easeOut' }}
                  className="font-karma font-extrabold leading-none whitespace-nowrap"
                  style={{
                    color: EXPLORATION_FLOATING_FEEDBACK_COLORS[item.tone] || EXPLORATION_FLOATING_FEEDBACK_COLORS.damage,
                    fontSize: isMobile ? '24px' : '30px',
                    letterSpacing: '0.3px',
                    textShadow:
                      '1px 0 0 rgba(0,0,0,1), -1px 0 0 rgba(0,0,0,1), 0 1px 0 rgba(0,0,0,1), 0 -1px 0 rgba(0,0,0,1), 1px 1px 0 rgba(0,0,0,1), -1px 1px 0 rgba(0,0,0,1), 1px -1px 0 rgba(0,0,0,1), -1px -1px 0 rgba(0,0,0,1), 0 8px 18px rgba(0,0,0,0.72)',
                  }}
                >
                  {item.text}
                </motion.div>
              </div>
            );
          })}
        </AnimatePresence>
      </div>

      <div
        className="absolute text-red-500 font-bold text-xl md:text-2xl font-karma pointer-events-none select-none"
        style={{ top: '54%', left: '19.5%', transform: 'translateX(-50%)' }}
      >
        {hp}
      </div>
      <div
        className="absolute pointer-events-none select-none"
        style={{ top: '15.8%', left: '80.3%', transform: 'translateX(-50%)' }}
      >
        {sanityHudGlitchBurst && sanityFx?.collapseActive && (
          <div
            className="absolute inset-0 text-fuchsia-200 font-bold text-xl md:text-2xl font-karma pointer-events-none"
            style={{
              opacity: sanityHudGlitchBurst.ghostOpacity,
              transform: `translate(${sanityHudGlitchBurst.ghostShiftX}px, ${sanityHudGlitchBurst.ghostShiftY}px)`,
              filter: 'blur(1.4px)',
            }}
          >
            {sp}
          </div>
        )}

        {sanityFx?.hudInstability > 0 && (
          <motion.div
            className="absolute inset-0 text-fuchsia-300 font-bold text-xl md:text-2xl font-karma"
            style={{
              opacity: 0.10 + (sanityFx.hudInstability || 0) * 0.28,
              filter: 'blur(1.2px)',
            }}
            animate={{
              x: [0, 1.5, -1, 0],
              y: [0, -0.5, 0.5, 0],
              opacity: [
                0.10 + (sanityFx.hudInstability || 0) * 0.18,
                0.18 + (sanityFx.hudInstability || 0) * 0.30,
                0.10 + (sanityFx.hudInstability || 0) * 0.18,
              ],
            }}
            transition={{
              duration: 1.35,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            {sp}
          </motion.div>
        )}

        <motion.div
          className="relative text-purple-400 font-bold text-xl md:text-2xl font-karma"
          animate={
            sanityFx?.hudInstability > 0
              ? {
                  opacity: [1, 0.82, 1, 0.9, 1],
                  filter: [
                    'brightness(1)',
                    'brightness(1.15)',
                    'brightness(1)',
                    'brightness(0.92)',
                    'brightness(1)',
                  ],
                }
              : undefined
          }
          transition={
            sanityFx?.hudInstability > 0
              ? {
                  duration: 1.2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }
              : undefined
          }
        >
          {sp}
        </motion.div>
      </div>

      <HudHoverTooltip
        open={!isMobile && isHovered}
        className="left-0 top-full mt-2 min-w-[170px] whitespace-nowrap"
      >
        <div className="flex flex-col gap-1">
          <div>
            <span className="font-bold text-red-400">HP</span>{' '}
            <span className="text-white">
              {hp}/{maxHp}
            </span>
          </div>
          <div>
            <span className="font-bold text-purple-400">SP</span>{' '}
            <span className="text-white">
              {sp}/{maxSp}
            </span>
          </div>
        </div>
      </HudHoverTooltip>
    </motion.div>
  );
};

const TorchDisplay = ({
  torches,
  maxTorches,
  onLightTorch,
  turns,
  torchDuration,
  highlight,
  isMobile,
  sanityHudGlitchBurst = null,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);

  const safeTorchDuration = Math.max(1, Number(torchDuration) || 1);
  const safeTurns = Math.max(0, Number(turns) || 0);
  const rawLevel = 1 - safeTurns / safeTorchDuration;
  const torchLevel = Math.max(0, rawLevel);
  const torchTurnsRemaining = Math.max(0, safeTorchDuration - safeTurns);
  const isLowTorch = torchLevel <= 0.15;
  const isTorchEmpty = torchLevel <= 0;
  const shouldPulse = highlight || isLowTorch;
  const torchGlowBackground = isTorchEmpty
    ? 'radial-gradient(circle, rgba(255, 92, 92, 0.96) 0%, rgba(210, 28, 28, 0.48) 45%, rgba(210, 28, 28, 0) 72%)'
    : 'radial-gradient(circle, rgba(255, 214, 120, 0.95) 0%, rgba(255, 170, 60, 0.40) 45%, rgba(255, 170, 60, 0) 72%)';

  return (
    <div
      className="relative"
      onMouseEnter={!isMobile ? () => setIsHovered(true) : undefined}
      onMouseLeave={!isMobile ? () => setIsHovered(false) : undefined}
    >
      <button
        type="button"
        onClick={(e) => stopBoundaryClick(e, onLightTorch)}
        className="relative bg-transparent border-0 p-0 shadow-none outline-none focus:outline-none active:scale-[.98] transition-transform duration-75"
        disabled={torches === 0 || torchLevel === 1}
      >
        <div className="relative w-[140px] h-[125px] flex items-center justify-center">
          <AnimatePresence>
            {shouldPulse && (
              <motion.div
                key={`torchGlow-${isTorchEmpty ? 'empty' : 'lit'}`}
                className="absolute -inset-8 pointer-events-none z-0"
                style={{
                  borderRadius: 9999,
                  background: torchGlowBackground,
                  filter: 'blur(22px)',
                }}
                initial={{ opacity: 0.0, scale: 0.88 }}
                animate={{ opacity: [0.25, 0.9, 0.25], scale: [0.88, 1.06, 0.88] }}
                transition={{ duration: 1.15, repeat: Infinity, ease: 'easeInOut' }}
                exit={{
                  opacity: 0.0,
                  scale: 0.92,
                  transition: { duration: 0.18, ease: 'easeOut', repeat: 0 },
                }}
              />
            )}
          </AnimatePresence>

          <div className="relative z-10">
            <TorchMeter
              level={torchLevel * 100}
              torches={torches}
              maxTorches={maxTorches}
              glitchBurst={sanityHudGlitchBurst}
            />
          </div>
        </div>
      </button>

<HudHoverTooltip
  open={!isMobile && isHovered}
  className="right-0 bottom-full mb-3 whitespace-nowrap"
>
        <div>
          <span className="font-bold text-yellow-400">Torch</span>{' '}
          <span className="text-white">
            {torchTurnsRemaining}/{safeTorchDuration}
          </span>
        </div>
      </HudHoverTooltip>
    </div>
  );
};

function ObjectiveBanner({
  text,
  imageSrc = textEnvelopImg,
  imageWidth = 'min(82vw, 360px)',
  imageMaxWidth = '360px',
  textTopOffset = '23px',
  textClassName = 'text-[26px] md:text-[42px] leading-[1.03]',
  textBoxStyle = {},
}) {
  const bannerTop = 'clamp(72px, 14vh, 132px)';

  return (
    <AnimatePresence>
      {!!text && (
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.99 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="fixed inset-0 pointer-events-none z-40"
        >
          <img
            src={imageSrc}
            alt=""
            aria-hidden="true"
            draggable={false}
            loading="lazy"
            className="absolute top-0 left-1/2 -translate-x-1/2 h-auto select-none"
            style={{
              top: bannerTop,
              width: imageWidth,
              maxWidth: imageMaxWidth,
            }}
          />

          <div
            className={`absolute left-1/2 -translate-x-1/2 text-center select-none ${textClassName}`}
            style={{
              top: `calc(${bannerTop} + ${textTopOffset})`,
              width: 'max-content',
              maxWidth: '92vw',
              fontFamily: 'Karma, serif',
              fontWeight: 700,
              color: 'rgba(245, 230, 200, 0.96)',
              textShadow:
                '0 2px 12px rgba(0,0,0,0.95), 0 0 18px rgba(0,0,0,0.55)',
              ...textBoxStyle,
            }}
          >
            <span className="block whitespace-normal md:whitespace-nowrap px-2 md:px-0">
              {text}
            </span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function GameHudOverlay({
  hidden,
  isMobile,
  lowFx,
  graphicsFxMode = 'full',
  floorState,
  vitalsState,
  inventoryState,
  relicState,
  actions,
}) {
  const explorationFlashSeqRef = React.useRef(0);
  const [damageFlashTick, setDamageFlashTick] = React.useState(0);
  const [healFlashTick, setHealFlashTick] = React.useState(0);
  const [spFlashTick, setSpFlashTick] = React.useState(0);

  const handleTriggerScreenFlash = React.useCallback((tone) => {
    explorationFlashSeqRef.current += 1;
    const tick = explorationFlashSeqRef.current;

    if (tone === 'damage') {
      setDamageFlashTick(tick);
      return;
    }

    if (tone === 'heal') {
      setHealFlashTick(tick);
      return;
    }

    if (tone === 'sp') {
      setSpFlashTick(tick);
    }
  }, []);

  const {
    floorLevel,
    floorMoodName,
    brazierCompletionBannerText,
    cleansingCompletionBannerText,
    corruptionBannerText,
    yahakaBannerText,
    loreBannerText,
    compassHintText,
  } = floorState;

  const [isMapHovered, setIsMapHovered] = React.useState(false);
  const currentFloorMoodLabel = floorMoodName || 'Unknown mood';

  const objectiveBannerQueueRef = React.useRef([]);
  const objectiveBannerTimeoutRef = React.useRef(null);
  const objectiveBannerNextTimeoutRef = React.useRef(null);
  const objectiveBannerSeqRef = React.useRef(0);
  const previousObjectiveTextsRef = React.useRef({
    brazier: '',
    cleansing: '',
    corruption: '',
    yahaka: '',
    lore: '',
  });
  const activeObjectiveBannerRef = React.useRef(null);
  const [activeObjectiveBanner, setActiveObjectiveBanner] = React.useState(null);

  const clearObjectiveBannerTimers = React.useCallback(() => {
    if (objectiveBannerTimeoutRef.current) {
      window.clearTimeout(objectiveBannerTimeoutRef.current);
      objectiveBannerTimeoutRef.current = null;
    }
    if (objectiveBannerNextTimeoutRef.current) {
      window.clearTimeout(objectiveBannerNextTimeoutRef.current);
      objectiveBannerNextTimeoutRef.current = null;
    }
  }, []);

  const showNextObjectiveBanner = React.useCallback(() => {
    clearObjectiveBannerTimers();

    const nextBanner = objectiveBannerQueueRef.current.shift() || null;
    activeObjectiveBannerRef.current = nextBanner;
    setActiveObjectiveBanner(nextBanner);

    if (!nextBanner) return;

    objectiveBannerTimeoutRef.current = window.setTimeout(() => {
      activeObjectiveBannerRef.current = null;
      setActiveObjectiveBanner(null);
      objectiveBannerTimeoutRef.current = null;

      objectiveBannerNextTimeoutRef.current = window.setTimeout(() => {
        objectiveBannerNextTimeoutRef.current = null;
        showNextObjectiveBanner();
      }, OBJECTIVE_BANNER_EXIT_MS);
    }, OBJECTIVE_BANNER_DURATION_MS);
  }, [clearObjectiveBannerTimers]);

  React.useEffect(() => {
    const incomingBanners = [
      { source: 'brazier', text: brazierCompletionBannerText },
      { source: 'cleansing', text: cleansingCompletionBannerText },
      { source: 'corruption', text: corruptionBannerText },
      { source: 'yahaka', text: yahakaBannerText },
      { source: 'lore', text: loreBannerText },
    ];

    let enqueuedAny = false;

    for (const { source, text } of incomingBanners) {
      const normalizedText = typeof text === 'string' ? text.trim() : '';
      const previousText = previousObjectiveTextsRef.current[source] || '';

      if (normalizedText && normalizedText !== previousText) {
        objectiveBannerSeqRef.current += 1;
        objectiveBannerQueueRef.current.push({
          id: `objective-banner-${objectiveBannerSeqRef.current}`,
          source,
          text: normalizedText,
          ...OBJECTIVE_BANNER_CONFIG_BY_SOURCE[source],
        });
        enqueuedAny = true;
      }

      previousObjectiveTextsRef.current[source] = normalizedText;
    }

    if (
      enqueuedAny
      && !activeObjectiveBannerRef.current
      && !objectiveBannerTimeoutRef.current
      && !objectiveBannerNextTimeoutRef.current
    ) {
      showNextObjectiveBanner();
    }
  }, [
    brazierCompletionBannerText,
    cleansingCompletionBannerText,
    corruptionBannerText,
    yahakaBannerText,
    loreBannerText,
    showNextObjectiveBanner,
  ]);

  React.useEffect(() => () => {
    clearObjectiveBannerTimers();
    objectiveBannerQueueRef.current = [];
    activeObjectiveBannerRef.current = null;
  }, [clearObjectiveBannerTimers]);

const { hp, maxHp, sp, maxSp, sanityFx, sanityHudGlitchBurst } = vitalsState;

  const {
    keys,
    maxKeys,
    teeth,
    maxTeeth,
    potions,
    maxPotions,
    torches,
    maxTorches,
    turns,
    torchDuration,
    showSpawnTorchHint,
    isBackpackOpen,
  } = inventoryState;

  const backpackFeedbackSeqRef = React.useRef(0);
  const backpackFeedbackQueueRef = React.useRef([]);
  const backpackFeedbackTimeoutRef = React.useRef(null);
  const prevBackpackResourcesRef = React.useRef(null);
  const [activeBackpackFeedbackItem, setActiveBackpackFeedbackItem] = React.useState(null);

  const showNextBackpackFeedback = React.useCallback(() => {
    if (backpackFeedbackTimeoutRef.current) {
      window.clearTimeout(backpackFeedbackTimeoutRef.current);
      backpackFeedbackTimeoutRef.current = null;
    }

    const nextItem = backpackFeedbackQueueRef.current.shift() || null;
    setActiveBackpackFeedbackItem(nextItem);

    if (!nextItem) return;

    backpackFeedbackTimeoutRef.current = window.setTimeout(() => {
      setActiveBackpackFeedbackItem(null);
      backpackFeedbackTimeoutRef.current = null;
      showNextBackpackFeedback();
    }, BACKPACK_FEEDBACK_DURATION_MS);
  }, []);

  const addBackpackFeedback = React.useCallback((resource, delta) => {
    if (!delta) return;

    backpackFeedbackSeqRef.current += 1;
    const item = {
      id: `backpack-float-${backpackFeedbackSeqRef.current}`,
      resource,
      text: `${delta > 0 ? '+' : ''}${delta}`,
      tone: delta > 0 ? 'gain' : 'loss',
    };

    backpackFeedbackQueueRef.current.push(item);

    if (!activeBackpackFeedbackItem && !backpackFeedbackTimeoutRef.current) {
      showNextBackpackFeedback();
    }
  }, [activeBackpackFeedbackItem, showNextBackpackFeedback]);

  React.useEffect(() => {
    return () => {
      if (backpackFeedbackTimeoutRef.current) {
        window.clearTimeout(backpackFeedbackTimeoutRef.current);
      }
      backpackFeedbackQueueRef.current = [];
    };
  }, []);

  React.useEffect(() => {
    const nextResources = {
      keys: Math.max(0, Math.floor(Number(keys) || 0)),
      teeth: Math.max(0, Math.floor(Number(teeth) || 0)),
      potions: Math.max(0, Math.floor(Number(potions) || 0)),
    };

    const prevResources = prevBackpackResourcesRef.current;

    if (!prevResources) {
      prevBackpackResourcesRef.current = nextResources;
      return;
    }

    const keyDelta = nextResources.keys - prevResources.keys;
    const teethDelta = nextResources.teeth - prevResources.teeth;
    const potionDelta = nextResources.potions - prevResources.potions;

    if (keyDelta !== 0) addBackpackFeedback('keys', keyDelta);
    if (teethDelta !== 0) addBackpackFeedback('teeth', teethDelta);
    if (potionDelta !== 0) addBackpackFeedback('potions', potionDelta);

    prevBackpackResourcesRef.current = nextResources;
  }, [keys, teeth, potions, addBackpackFeedback]);

  const positionedOpenBackpackFeedbackItems = React.useMemo(
    () => (activeBackpackFeedbackItem ? positionBackpackFloatingFeedback([activeBackpackFeedbackItem]) : []),
    [activeBackpackFeedbackItem]
  );

  const positionedClosedBackpackFeedbackItems = React.useMemo(
    () => (activeBackpackFeedbackItem ? positionClosedBackpackFloatingFeedback([activeBackpackFeedbackItem]) : []),
    [activeBackpackFeedbackItem]
  );

  const getOpenBackpackFeedbackItems = React.useCallback(
    (resource) => positionedOpenBackpackFeedbackItems.filter((item) => item.resource === resource),
    [positionedOpenBackpackFeedbackItems]
  );

  const {
    ownedRelics,
    hoveredRelic,
    mobileRelicInfo,
    trinketLimit,
  } = relicState;

  const visibleRelics = React.useMemo(
    () => ownedRelics.slice(0, trinketLimit),
    [ownedRelics, trinketLimit]
  );

  const desktopRelicDisplayMetrics = React.useMemo(() => {
    const count = visibleRelics.length;
    const compact = count > 20 || trinketLimit > 20;
    const columns = compact ? 8 : 10;
    const iconSize = compact ? 36 : 40;
    const gap = compact ? 6 : 8;
    const rows = Math.max(1, Math.ceil(Math.max(count, 1) / columns));
    const stripHeight = rows * iconSize + Math.max(0, rows - 1) * gap;
    const stripWidth = columns * iconSize + Math.max(0, columns - 1) * gap;
    const tooltipBottom = 18 + stripHeight + 22;
    return { columns, iconSize, gap, rows, stripWidth, tooltipBottom };
  }, [trinketLimit, visibleRelics]);

  const mobileRelicDisplayMetrics = React.useMemo(() => {
    const count = visibleRelics.length;
    const doubleColumn = count > 10 || trinketLimit > 20;
    const columns = doubleColumn ? 2 : 1;
    const iconSize = doubleColumn ? 28 : 32;
    const gap = doubleColumn ? 6 : 8;
    const stripWidth = columns * iconSize + Math.max(0, columns - 1) * gap;
    return { columns, iconSize, gap, stripWidth };
  }, [trinketLimit, visibleRelics]);

  if (hidden) return null;

  return (
    <>
      <AnimatePresence initial={false}>
        {damageFlashTick > 0 && <ExplorationScreenFlash tick={damageFlashTick} tone="damage" />}
      </AnimatePresence>
      <AnimatePresence initial={false}>
        {healFlashTick > 0 && <ExplorationScreenFlash tick={healFlashTick} tone="heal" />}
      </AnimatePresence>
      <AnimatePresence initial={false}>
        {spFlashTick > 0 && <ExplorationScreenFlash tick={spFlashTick} tone="sp" />}
      </AnimatePresence>
      <div
        aria-hidden="true"
        className="fixed inset-0 z-30 pointer-events-none overflow-hidden select-none"
      >
        <img
  src={topLeftUiDecoration}
  alt=""
  draggable={false}
  loading="lazy"
  className="absolute top-0 left-0 pointer-events-none select-none opacity-30"
/>
<img
  src={bottomRightUiDecoration}
  alt=""
  draggable={false}
  loading="lazy"
  className="absolute bottom-0 right-0 pointer-events-none select-none opacity-30"
/>

      </div>

      <motion.div
  data-ui="true"
  className="absolute top-0 left-0 w-full flex justify-between items-start p-2 md:p-4 z-40 pointer-events-none"
  animate={
    sanityHudGlitchBurst
      ? { opacity: 1 - (sanityHudGlitchBurst.dimAlpha || 0) }
      : { opacity: 1 }
  }
  transition={{ duration: 0.08, ease: 'easeOut' }}
>
        <div className="pointer-events-auto">
<HealthDisplay
  hp={hp}
  maxHp={maxHp}
  sp={sp}
  maxSp={maxSp}
  sanityFx={sanityFx}
  sanityHudGlitchBurst={sanityHudGlitchBurst}
  isMobile={isMobile}
  onOpenStatus={actions.onOpenStatus}
  onTriggerScreenFlash={handleTriggerScreenFlash}
/>
        </div>

        <ObjectiveBanner
          text={activeObjectiveBanner?.text || ''}
          imageSrc={activeObjectiveBanner?.imageSrc || textEnvelopImg}
          imageWidth={activeObjectiveBanner?.imageWidth || 'min(82vw, 360px)'}
          imageMaxWidth={activeObjectiveBanner?.imageMaxWidth || '360px'}
          textTopOffset={activeObjectiveBanner?.textTopOffset || '23px'}
          textClassName={activeObjectiveBanner?.textClassName || 'text-[26px] md:text-[42px] leading-[1.03]'}
          textBoxStyle={activeObjectiveBanner?.textBoxStyle || {}}
        />

        <div className="flex flex-col items-end gap-2 pointer-events-auto">
          
          <button
            type="button"
            onClick={actions.onResetDemo}
            className="px-3 py-1.5 rounded-lg bg-black/60 text-white font-karma text-sm border border-white/20 hover:bg-black/80 active:scale-[.98] transition"
            title="Clears saved trinkets + deck and restarts the run"
          >
            Reset demo
          </button>

          <button
            type="button"
            onClick={actions.onToggleGraphicsFxMode}
            className="mt-3 px-3 py-1.5 rounded-lg bg-black/60 text-white font-karma text-sm border border-white/20 hover:bg-black/80 active:scale-[.98] transition"
            title={isMobile ? 'Mobile defaults to Mid FX. Tap to cycle between Full, Mid and Low FX.' : 'Cycle between Full, Mid and Low FX.'}
          >
            FX: {graphicsFxMode === 'full' ? 'Full' : graphicsFxMode === 'mid' ? 'Mid' : 'Low'}
          </button>

          <div
            className="mt-3 relative"
            onMouseEnter={!isMobile ? () => setIsMapHovered(true) : undefined}
            onMouseLeave={!isMobile ? () => setIsMapHovered(false) : undefined}
          >
            <button
              type="button"
              onClick={actions.onOpenMiniMap}
              className="relative bg-transparent border-0 p-0 shadow-none outline-none focus:outline-none transition-transform hover:scale-105 active:scale-95"
              title="Open mini-map"
            >
              <img
                src={sanityHudGlitchBurst ? minimapGlitchImg : minimapIconImg}
                alt="Mini-map"
                draggable={false}
                loading="lazy"
                className="w-[56px] h-[62px] md:w-[72px] md:h-[80px]"
              />
            </button>

            <HudHoverTooltip
              open={!isMobile && isMapHovered}
              className="right-0 top-full mt-2 min-w-[170px] whitespace-nowrap text-left"
            >
              <div className="flex flex-col gap-1">
                <div>
                  <span className="font-bold text-yellow-400">Floor</span>{' '}
                  <span className="text-white">{floorLevel}</span>
                </div>
                <div className="text-white">{currentFloorMoodLabel}</div>
              </div>
            </HudHoverTooltip>
          </div>

          <button
            type="button"
            onClick={actions.onOpenLegendarium}
            className="relative bg-transparent border-0 p-0 shadow-none outline-none focus:outline-none transition-transform hover:scale-105 active:scale-95"
            title="Open Legendarium"
          >
            <img
              src={sanityHudGlitchBurst ? legendariumGlitchImg : legendariumIconImg}
              alt="Legendarium"
              draggable={false}
              loading="lazy"
              className="w-[56px] h-[62px] md:w-[72px] md:h-[80px]"
            />
          </button>
        </div>
      </motion.div>

      <div
        data-ui="true"
        className="fixed z-40 flex items-end"
        style={{
          right: isMobile ? 'calc(110px + 0.5rem)' : 'calc(140px + 1rem)',
          bottom: isMobile ? '0.75rem' : '1.25rem',
        }}
      >
        <div className="relative mr-2">
          <button
            type="button"
            onClick={actions.onToggleBackpack}
            className="relative z-10 bg-transparent border-0 p-0 shadow-none outline-none focus:outline-none transition-transform hover:scale-105 active:scale-95"
          >
            <img
              src={sanityHudGlitchBurst ? backpackGlitchIcon : backpackIcon}
              alt="Backpack"
              draggable={false}
              loading="lazy"
              className="w-[56px] h-[62px] md:w-[72px] md:h-[80px]"
            />
          </button>

          {!isBackpackOpen && positionedClosedBackpackFeedbackItems.length > 0 && (
            <div
              className="absolute left-1/2 z-20 pointer-events-none"
              style={{ bottom: '100%', marginBottom: isMobile ? '10px' : '14px' }}
              aria-hidden="true"
            >
              <BackpackFloatingFeedback
                items={positionedClosedBackpackFeedbackItems}
                isMobile={isMobile}
                withIcon
              />
            </div>
          )}

          <AnimatePresence>
            {isBackpackOpen && (
              <motion.div
                className="absolute bottom-[70px] left-0 w-[140px] h-[90px]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <motion.div
                  className="absolute"
                  style={{ top: '70px', left: '-145px' }}
                  initial={{ scale: 0, opacity: 0, x: 0, y: 0 }}
                  animate={{
                    scale: 1,
                    opacity: 1,
                    x: 0,
                    y: 0,
                    transition: {
                      delay: 0.1,
                      type: 'spring',
                      stiffness: 300,
                      damping: 20,
                    },
                  }}
                  exit={{
                    scale: 0,
                    opacity: 0,
                    x: 0,
                    y: 0,
                    transition: { duration: 0.2 },
                  }}
                >
                  <div className="relative flex flex-col items-center">
                    <div
                      className="absolute left-1/2 z-10 pointer-events-none"
                      style={{ top: '-8px' }}
                      aria-hidden="true"
                    >
                      <BackpackFloatingFeedback
                        items={getOpenBackpackFeedbackItems('keys')}
                        isMobile={isMobile}
                      />
                    </div>
                    <img
                      src={keyIcon}
                      style={{ width: '48px', height: '44px' }}
                      className="mb-1"
                      alt="Keys"
                      draggable={false}
                      loading="lazy"
                    />
                    <span className="text-yellow-300 text-l bg-black bg-opacity-90 px-2 py-1 rounded border border-yellow-600">
                      {keys}/{maxKeys}
                    </span>
                  </div>
                </motion.div>

                <motion.div
                  className="absolute"
                  style={{ top: '70px', left: '-95px' }}
                  initial={{ scale: 0, opacity: 0, x: 0, y: 0 }}
                  animate={{
                    scale: 1,
                    opacity: 1,
                    x: 0,
                    y: 0,
                    transition: {
                      delay: 0.2,
                      type: 'spring',
                      stiffness: 300,
                      damping: 20,
                    },
                  }}
                  exit={{
                    scale: 0,
                    opacity: 0,
                    x: 0,
                    y: 0,
                    transition: { duration: 0.2 },
                  }}
                >
                  <div className="relative flex flex-col items-center">
                    <div
                      className="absolute left-1/2 z-10 pointer-events-none"
                      style={{ top: '-8px' }}
                      aria-hidden="true"
                    >
                      <BackpackFloatingFeedback
                        items={getOpenBackpackFeedbackItems('teeth')}
                        isMobile={isMobile}
                      />
                    </div>
                    <img
                      src={teethIcon}
                      style={{ width: '48px', height: '44px' }}
                      className="mb-1"
                      alt="Teeth"
                      draggable={false}
                      loading="lazy"
                    />
                    <span className="text-white text-l bg-black bg-opacity-90 px-2 py-1 rounded border border-gray-500">
                      {teeth}/{maxTeeth}
                    </span>
                  </div>
                </motion.div>

                <motion.div
                  className="absolute"
                  style={{ top: '70px', right: '140px' }}
                  initial={{ scale: 0, opacity: 0, x: 0, y: 0 }}
                  animate={{
                    scale: 1,
                    opacity: 1,
                    x: 0,
                    y: 0,
                    transition: {
                      delay: 0.3,
                      type: 'spring',
                      stiffness: 300,
                      damping: 20,
                    },
                  }}
                  exit={{
                    scale: 0,
                    opacity: 0,
                    x: 0,
                    y: 0,
                    transition: { duration: 0.2 },
                  }}
                >
                  <button
                    type="button"
                    onClick={(e) => stopBoundaryClick(e, actions.onUsePotion)}
                    onPointerDown={stopBoundaryPointerDown}
                    disabled={potions <= 0 || hp >= maxHp}
                    title={
                      potions <= 0
                        ? 'No potions'
                        : hp >= maxHp
                          ? 'HP is already full'
                          : 'Use 1 potion (+60 HP)'
                    }
                    className="flex flex-col items-center bg-transparent border-0 p-0 shadow-none outline-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-transform hover:scale-105 active:scale-95"
                  >
                    <div className="relative flex flex-col items-center">
                      <div
                        className="absolute left-1/2 z-10 pointer-events-none"
                        style={{ top: '-8px' }}
                        aria-hidden="true"
                      >
                        <BackpackFloatingFeedback
                          items={getOpenBackpackFeedbackItems('potions')}
                          isMobile={isMobile}
                        />
                      </div>
                      <div style={{ width: '44px', height: '44px', marginBottom: '0.25rem' }}>
                        <img
                          src={potionIcon}
                          alt="Potions"
                          draggable={false}
                          loading="lazy"
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      </div>
                      <span className="text-blue-300 text-l bg-black bg-opacity-90 px-2 py-1 rounded border border-blue-400">
                        {potions}/{maxPotions}
                      </span>
                    </div>
                  </button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="fixed bottom-3 right-3 z-40 md:bottom-7 md:right-4">
        <div className="scale-[.78] md:scale-100 origin-bottom-right">
<TorchDisplay
  torches={torches}
  maxTorches={maxTorches}
  turns={turns}
  torchDuration={torchDuration}
  highlight={showSpawnTorchHint}
  isMobile={isMobile}
  onLightTorch={actions.onLightTorch}
  sanityHudGlitchBurst={sanityHudGlitchBurst}
/>
        </div>
      </div>

      {ownedRelics.length > 0 && (
        <>
          {!isMobile && (
            <AnimatePresence>
              {hoveredRelic && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.15 }}
                  className="fixed z-50 bg-black bg-opacity-80 p-3 rounded-lg border border-yellow-500 flex items-center gap-3 max-w-[360px]"
                  style={{
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bottom: `${desktopRelicDisplayMetrics.tooltipBottom}px`,
                  }}
                >
                  <img
                    src={hoveredRelic.image}
                    alt={hoveredRelic.name}
                    className="w-12 h-12 object-contain"
                    draggable={false}
                  />
                  <div>
                    <div className="font-bold text-yellow-400">{hoveredRelic.name}</div>
                    <div className="text-[11px] uppercase tracking-widest text-gray-200/70">
                      {formatRelicRarity(getRelicRarity(hoveredRelic))}
                    </div>
                    <div className="text-sm text-gray-300">{hoveredRelic.description}</div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          )}

          {isMobile && (
            <AnimatePresence>
              {mobileRelicInfo && (
                <>
                  <motion.button
                    type="button"
                    className="fixed inset-0 z-50 bg-transparent"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={actions.onCloseMobileRelicInfo}
                  />

                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 12 }}
                    transition={{ duration: 0.15 }}
                    className="fixed z-60 left-1/2 bottom-[110px] -translate-x-1/2 bg-black bg-opacity-80 p-3 rounded-lg border border-yellow-500 flex items-center gap-3 max-w-[360px]"
                    onClick={(e) => stopBoundaryClick(e)}
                  >
                    <img
                      src={mobileRelicInfo.image}
                      alt={mobileRelicInfo.name}
                      className="w-12 h-12 object-contain"
                      draggable={false}
                    />
                    <div>
                      <div className="font-bold text-yellow-400">{mobileRelicInfo.name}</div>
                      <div className="text-[11px] uppercase tracking-widest text-gray-200/70">
                        {formatRelicRarity(getRelicRarity(mobileRelicInfo))}
                      </div>
                      <div className="text-sm text-gray-300">{mobileRelicInfo.description}</div>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          )}

          {!isMobile ? (
            <div
              className="fixed z-40"
              style={{
                left: '50%',
                transform: 'translateX(-50%)',
                bottom: '18px',
                pointerEvents: 'auto',
              }}
            >
              <div
                className="grid"
                style={{
                  width: `${desktopRelicDisplayMetrics.stripWidth}px`,
                  gridTemplateColumns: `repeat(${desktopRelicDisplayMetrics.columns}, minmax(0, 1fr))`,
                  gap: `${desktopRelicDisplayMetrics.gap}px`,
                  justifyItems: 'center',
                }}
              >
                {visibleRelics.map((relic) => (
                  <div
                    key={relic.id}
                    className="cursor-pointer"
                    onMouseEnter={() => actions.onHoverRelic(relic)}
                    onMouseLeave={actions.onLeaveRelic}
                  >
                    <img
                      src={relic.image}
                      alt={relic.name}
                      className="object-contain"
                      style={{ width: `${desktopRelicDisplayMetrics.iconSize}px`, height: `${desktopRelicDisplayMetrics.iconSize}px` }}
                      draggable={false}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div
              className="fixed z-30 grid"
              style={{
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'auto',
                width: `${mobileRelicDisplayMetrics.stripWidth}px`,
                gridTemplateColumns: `repeat(${mobileRelicDisplayMetrics.columns}, minmax(0, 1fr))`,
                gap: `${mobileRelicDisplayMetrics.gap}px`,
                justifyItems: 'center',
              }}
            >
              {visibleRelics.map((relic) => (
                <button
                  key={relic.id}
                  type="button"
                  className="p-0 m-0 bg-transparent border-0"
                  onClick={(e) =>
                    stopBoundaryClick(e, () => actions.onToggleMobileRelicInfo(relic))
                  }
                >
                  <img
                    src={relic.image}
                    alt={relic.name}
                    className="object-contain"
                    style={{ width: `${mobileRelicDisplayMetrics.iconSize}px`, height: `${mobileRelicDisplayMetrics.iconSize}px` }}
                    draggable={false}
                  />
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
}

function areGameHudOverlayPropsEqual(prev, next) {
  return (
    prev.hidden === next.hidden &&
    prev.isMobile === next.isMobile &&
    prev.lowFx === next.lowFx &&
    prev.graphicsFxMode === next.graphicsFxMode &&
    prev.floorState === next.floorState &&
    prev.vitalsState === next.vitalsState &&
    prev.inventoryState === next.inventoryState &&
    prev.relicState === next.relicState &&
    prev.actions === next.actions
  );
}

export default React.memo(GameHudOverlay, areGameHudOverlayPropsEqual);
